import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  FormControlLabel,
  Alert,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Fade,
  Grow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Switch
} from '@mui/material';
import {
  PlaylistPlay as PlaylistIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Person as PersonIcon,
  VideoLibrary as VideoLibraryIcon,
  Schedule as ScheduleIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  CloudDownload as CloudDownloadIcon,
  Archive as ArchiveIcon,
  Speed as SpeedIcon,
  Audiotrack as AudioIcon,
  Subtitles as SubtitlesIcon
} from '@mui/icons-material';
import { useDownload } from '../contexts/DownloadContext';
import toast from 'react-hot-toast';

const PlaylistPage = () => {
  const { fetchMediaInfo, playlistInfo, loading, downloadMedia, downloadPlaylistItems } = useDownload();
  
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [quality, setQuality] = useState('1080p');
  const [format, setFormat] = useState('video');
  const [platform, setPlatform] = useState('youtube');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeSubtitles, setIncludeSubtitles] = useState(false);
  const [downloadAsZip, setDownloadAsZip] = useState(true);
  const [concurrentDownloads, setConcurrentDownloads] = useState(3);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFetchPlaylist = async () => {
    if (!playlistUrl.trim()) {
      toast.error('Please enter a playlist URL');
      return;
    }

    try {
      const info = await fetchMediaInfo(playlistUrl, 'playlist');
      setSelectedVideos(info.videos.map(video => video.id));
    } catch (error) {
      console.error('Failed to fetch playlist:', error);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedVideos(playlistInfo.videos.map(video => video.id));
    } else {
      setSelectedVideos([]);
    }
  };

  const handleSelectVideo = (videoId) => {
    setSelectedVideos(prev => {
      if (prev.includes(videoId)) {
        return prev.filter(id => id !== videoId);
      } else {
        return [...prev, videoId];
      }
    });
  };

  const handleDownloadSelected = async () => {
    if (!selectedVideos.length) {
      toast.error('Please select at least one video');
      return;
    }

    setDownloading(true);
    setDownloadProgress(0);

    try {
      if (downloadAsZip) {
        // Download as ZIP file
        await downloadPlaylistItems(playlistInfo.id, selectedVideos, {
          format,
          quality,
          platform,
          includeMetadata,
          includeSubtitles,
          concurrentDownloads
        });
      } else {
        // Download individually
        const selectedVideoInfo = playlistInfo.videos.filter(video => 
          selectedVideos.includes(video.id)
        );

        let completed = 0;
        const total = selectedVideoInfo.length;

        // Download with concurrency control
        const chunks = [];
        for (let i = 0; i < selectedVideoInfo.length; i += concurrentDownloads) {
          chunks.push(selectedVideoInfo.slice(i, i + concurrentDownloads));
        }

        for (const chunk of chunks) {
          await Promise.all(chunk.map(async (video) => {
            try {
              await downloadMedia(video.id, {
                format,
                quality,
                platform,
                includeMetadata,
                includeSubtitles
              });
              completed++;
              setDownloadProgress((completed / total) * 100);
            } catch (error) {
              console.error(`Failed to download ${video.title}:`, error);
              toast.error(`Failed to download: ${video.title}`);
            }
          }));
          
          // Small delay between chunks
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      toast.success(`Successfully started downloading ${selectedVideos.length} videos`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download playlist');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(playlistUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Playlist URL copied');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const paginatedVideos = playlistInfo?.videos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isAllSelected = playlistInfo && 
    selectedVideos.length === playlistInfo.videos.length;

  const totalDuration = playlistInfo?.videos.reduce(
    (sum, video) => sum + (video.duration || 0), 
    0
  );

  const totalSize = playlistInfo?.videos.reduce(
    (sum, video) => sum + (video.filesize || 0), 
    0
  );

  const formatBytes = (bytes) => {
    if (bytes === 0 || !bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Grow in={true} timeout={800}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            <PlaylistIcon sx={{ mr: 2, verticalAlign: 'middle', color: 'secondary.main' }} />
            Universal Playlist Downloader
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            Download entire playlists from YouTube, Spotify, SoundCloud, and more. Select specific videos,
            choose quality, and download as individual files or ZIP archive.
          </Typography>

          {/* URL Input */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              label="Playlist URL"
              placeholder="Paste any playlist URL here..."
              value={playlistUrl}
              onChange={(e) => setPlaylistUrl(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                }
              }}
              InputProps={{
                startAdornment: (
                  <PlaylistIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
            <Tooltip title="Copy URL">
              <IconButton onClick={handleCopyUrl} disabled={!playlistUrl}>
                {copied ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleFetchPlaylist}
              disabled={loading || !playlistUrl.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ minWidth: 150 }}
            >
              {loading ? 'Fetching...' : 'Get Playlist'}
            </Button>
          </Box>

          {/* Download Options */}
          <Accordion 
            expanded={showAdvanced} 
            onChange={() => setShowAdvanced(!showAdvanced)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                <Typography>Advanced Download Settings</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Format</InputLabel>
                    <Select
                      value={format}
                      onChange={(e) => {
                        setFormat(e.target.value);
                        if (e.target.value === 'audio') {
                          setQuality('192kbps');
                        }
                      }}
                      label="Format"
                      size="medium"
                    >
                      <MenuItem value="video">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VideoLibraryIcon /> Video (MP4)
                        </Box>
                      </MenuItem>
                      <MenuItem value="audio">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AudioIcon /> Audio (MP3)
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Quality</InputLabel>
                    <Select
                      value={quality}
                      onChange={(e) => setQuality(e.target.value)}
                      label="Quality"
                      size="medium"
                    >
                      {format === 'video' ? [
                        <MenuItem key="best" value="best">Best Available</MenuItem>,
                        <MenuItem key="2160p" value="2160p">4K (2160p)</MenuItem>,
                        <MenuItem key="1440p" value="1440p">2K (1440p)</MenuItem>,
                        <MenuItem key="1080p" value="1080p">1080p (Full HD)</MenuItem>,
                        <MenuItem key="720p" value="720p">720p (HD)</MenuItem>,
                        <MenuItem key="480p" value="480p">480p (SD)</MenuItem>
                      ] : [
                        <MenuItem key="best" value="best">Best Quality</MenuItem>,
                        <MenuItem key="320" value="320kbps">320 kbps</MenuItem>,
                        <MenuItem key="256" value="256kbps">256 kbps</MenuItem>,
                        <MenuItem key="192" value="192kbps">192 kbps</MenuItem>,
                        <MenuItem key="128" value="128kbps">128 kbps</MenuItem>
                      ]}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Concurrent Downloads</InputLabel>
                    <Select
                      value={concurrentDownloads}
                      onChange={(e) => setConcurrentDownloads(e.target.value)}
                      label="Concurrent Downloads"
                      size="medium"
                    >
                      {[1, 2, 3, 5, 10].map(num => (
                        <MenuItem key={num} value={num}>{num}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={downloadAsZip}
                          onChange={(e) => setDownloadAsZip(e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ArchiveIcon />
                          <span>Download as ZIP archive</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeMetadata}
                          onChange={(e) => setIncludeMetadata(e.target.checked)}
                        />
                      }
                      label="Include metadata"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeSubtitles}
                          onChange={(e) => setIncludeSubtitles(e.target.checked)}
                          disabled={format === 'audio'}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SubtitlesIcon />
                          <span>Include subtitles</span>
                        </Box>
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Grow>

      {/* Playlist Content */}
      {playlistInfo ? (
        <Fade in={true} timeout={1000}>
          <Paper sx={{ p: 3 }}>
            {/* Playlist Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {playlistInfo.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<PersonIcon />}
                    label={playlistInfo.author?.name || 'Unknown Author'}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<VideoLibraryIcon />}
                    label={`${playlistInfo.videos.length} videos`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<ScheduleIcon />}
                    label={formatDuration(totalDuration)}
                    size="small"
                    variant="outlined"
                  />
                  {totalSize > 0 && (
                    <Chip
                      icon={<CloudDownloadIcon />}
                      label={`Total: ${formatBytes(totalSize)}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedVideos.length} / {playlistInfo.videos.length}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleDownloadSelected}
                  disabled={selectedVideos.length === 0 || downloading}
                  size="large"
                  sx={{
                    background: 'linear-gradient(45deg, #FF3B30 30%, #FF6B4A 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FF3B30 20%, #FF6B4A 80%)',
                    }
                  }}
                >
                  {downloading ? 'Downloading...' : `Download (${selectedVideos.length})`}
                </Button>
              </Box>
            </Box>

            {/* Download Progress */}
            {downloading && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Downloading playlist...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(downloadProgress)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={downloadProgress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'background.default',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(45deg, #FF3B30 30%, #FF6B4A 90%)',
                    }
                  }}
                />
              </Box>
            )}

            {/* Selection Controls */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    indeterminate={
                      selectedVideos.length > 0 &&
                      selectedVideos.length < playlistInfo.videos.length
                    }
                  />
                }
                label="Select All Videos"
              />
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" color="text.secondary">
                {downloadAsZip ? 'Will be downloaded as ZIP archive' : 'Each video downloads separately'}
              </Typography>
            </Box>

            {/* Videos Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" width="50px">
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        indeterminate={
                          selectedVideos.length > 0 &&
                          selectedVideos.length < playlistInfo.videos.length
                        }
                      />
                    </TableCell>
                    <TableCell width="80px">#</TableCell>
                    <TableCell width="160px">Thumbnail</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell width="100px">Duration</TableCell>
                    <TableCell width="100px">Size</TableCell>
                    <TableCell width="120px">Format</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedVideos.map((video) => (
                    <TableRow key={video.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckBoxIcon />}
                          checked={selectedVideos.includes(video.id)}
                          onChange={() => handleSelectVideo(video.id)}
                        />
                      </TableCell>
                      <TableCell>{video.index || (page * rowsPerPage + paginatedVideos.indexOf(video) + 1)}</TableCell>
                      <TableCell>
                        {video.thumbnail && (
                          <CardMedia
                            component="img"
                            sx={{ 
                              width: 120, 
                              height: 68, 
                              borderRadius: 1,
                              objectFit: 'cover'
                            }}
                            image={video.thumbnail}
                            alt={video.title}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 400 }}>
                          {video.title}
                        </Typography>
                        {video.uploader && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {video.uploader}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={video.durationFormatted || formatDuration(video.duration)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {video.filesize && (
                          <Typography variant="caption">
                            {formatBytes(video.filesize)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={format === 'video' ? 'MP4' : 'MP3'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={playlistInfo.videos.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Download Summary */}
            <Alert 
              severity="info" 
              sx={{ mt: 3 }}
              icon={<DownloadIcon />}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Box>
                  <Typography variant="body2">
                    Ready to download {selectedVideos.length} videos as {format === 'video' ? 'MP4' : 'MP3'} ({quality})
                  </Typography>
                  {selectedVideos.length > 0 && totalSize > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Estimated total size: {formatBytes(totalSize * (selectedVideos.length / playlistInfo.videos.length))}
                    </Typography>
                  )}
                </Box>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleDownloadSelected}
                  disabled={selectedVideos.length === 0 || downloading}
                  sx={{ ml: 2 }}
                >
                  Download Now
                </Button>
              </Box>
            </Alert>
          </Paper>
        </Fade>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <PlaylistIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
            No Playlist Selected
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto' }}>
            Enter any playlist URL above to see its contents and start downloading videos
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Examples: YouTube playlists, Spotify albums, SoundCloud sets, and more
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default PlaylistPage;